// Stage catalogue editor inside the Manage Team modal. Lists every
// stage from production_stage_configs (canonical + custom + archived),
// lets the user rename / recolour / toggle hasHours / archive each
// row, and adds new custom stages via the inline form. Mutations
// hit /api/.../stages and refresh the catalogue via the StagesProvider.
import { useEffect, useState } from "react";
import type {
  StageConfigInputT,
  StageDefDto,
} from "../../../../../../shared/types/production-schedule";
import {
  createStageConfig,
  deleteStageConfig,
  reorderStageConfigs,
  updateStageConfig,
} from "../hooks/useProductionApi";
import { useStagesCatalogue } from "../components/StagesProvider";

export function StageConfigsSection() {
  const { stages, refetch } = useStagesCatalogue();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);

  const visible = showArchived ? stages : stages.filter((s) => !s.archived);

  // Drag-and-drop reorder. Drops the dragged row directly before
  // the drop target's row, then ships the full ordered key list to
  // /stages/reorder. Refetches via the StagesProvider so every
  // other consumer (Gantt, SpoolCard pills, Resource Management)
  // picks up the new order without a page reload.
  //
  // The persisted catalogue is the source of truth for order — we
  // intentionally don't apply an optimistic local reorder, since a
  // failed server call would otherwise show a phantom order until
  // the next provider refresh.
  const onDrop = async (targetKey: string) => {
    setDropTargetKey(null);
    const source = dragKey;
    setDragKey(null);
    if (!source || source === targetKey) return;
    // Build the new order from the *full* catalogue (incl. archived
    // rows the user hid) so the persisted sort_order keeps every
    // row anchored. The reorder UI is filtered to `visible`, but
    // we don't want to renumber only some of the catalogue and
    // leave archived rows with stale positions.
    const sourceIdx = stages.findIndex((s) => s.stageKey === source);
    const targetIdx = stages.findIndex((s) => s.stageKey === targetKey);
    if (sourceIdx < 0 || targetIdx < 0) return;
    const next = stages.slice();
    const [moved] = next.splice(sourceIdx, 1);
    if (!moved) return;
    // Drop *before* the target. When dragging downward the target's
    // new index shifts up by 1 after removing the source — splice
    // already accounts for that since we worked on the spliced array.
    const insertAt = next.findIndex((s) => s.stageKey === targetKey);
    next.splice(insertAt < 0 ? next.length : insertAt, 0, moved);
    setError(null);
    setBusyKey(source);
    try {
      await reorderStageConfigs(next.map((s) => s.stageKey));
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const patch = async (stageKey: string, body: Record<string, unknown>) => {
    setBusyKey(stageKey);
    setError(null);
    try {
      await updateStageConfig(stageKey, body);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const archive = async (stageKey: string) => {
    const target = stages.find((s) => s.stageKey === stageKey);
    if (!target) return;
    const verb = target.archived ? "restore" : "archive";
    if (!window.confirm(`${verb[0]?.toUpperCase()}${verb.slice(1)} ${target.name}?`)) return;
    setBusyKey(stageKey);
    setError(null);
    try {
      if (target.archived) {
        await updateStageConfig(stageKey, { archived: false });
      } else {
        await deleteStageConfig(stageKey);
      }
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const add = async (input: StageConfigInputT) => {
    setError(null);
    try {
      await createStageConfig(input);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
        paddingBottom: 8,
        borderBottom: "1px solid var(--bsp-border)",
      }}>
        <span style={{
          fontFamily: "var(--bsp-font-display)",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--bsp-blue)",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}>Stage catalogue</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>
      <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Rename, recolour, or toggle hours-tracking on each stage. Archive
        hides a stage from new spools; existing spool stage rows keep
        their reference so historical data stays intact, and a stage can
        be restored at any time.
      </p>
      {error ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
          {error}
        </div>
      ) : null}
      <table className="stages-table">
        <thead>
          <tr>
            <th style={{ width: 24 }} title="Drag to reorder" />
            <th style={{ width: 60 }}>Short</th>
            <th>Name</th>
            <th style={{ width: 80 }}>Colour</th>
            <th style={{ width: 70 }}>Hours</th>
            <th style={{ width: 110 }} title="Hours this station can soak up per week — drives Resource Management heatmap">Capacity (h/wk)</th>
            <th style={{ width: 90 }}>Type</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {visible.map((def) => (
            <StageRow
              key={def.stageKey}
              def={def}
              busy={busyKey === def.stageKey}
              dragging={dragKey === def.stageKey}
              dropTarget={dropTargetKey === def.stageKey}
              onPatch={(body) => patch(def.stageKey, body)}
              onArchive={() => archive(def.stageKey)}
              onDragStart={() => setDragKey(def.stageKey)}
              onDragOver={() => {
                if (dragKey && dragKey !== def.stageKey && dropTargetKey !== def.stageKey) {
                  setDropTargetKey(def.stageKey);
                }
              }}
              onDragLeave={() => {
                if (dropTargetKey === def.stageKey) setDropTargetKey(null);
              }}
              onDrop={() => void onDrop(def.stageKey)}
              onDragEnd={() => { setDragKey(null); setDropTargetKey(null); }}
            />
          ))}
        </tbody>
      </table>
      <AddStageForm onAdd={add} />
    </div>
  );
}

function StageRow({
  def,
  busy,
  dragging,
  dropTarget,
  onPatch,
  onArchive,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: {
  def: StageDefDto;
  busy: boolean;
  dragging: boolean;
  dropTarget: boolean;
  onPatch: (body: Record<string, unknown>) => void;
  onArchive: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const [short, setShort] = useState(def.short);
  const [name, setName] = useState(def.name);
  const [color, setColor] = useState(def.color);

  // The drag handle (left-most cell) is the only thing that toggles
  // draggable, so clicking inside the row's text fields doesn't
  // accidentally start a drag. The cell forwards the standard HTML5
  // drag events up to the parent's handlers.
  return (
    <tr
      className={def.archived ? "is-skipped" : ""}
      style={{
        opacity: dragging ? 0.4 : 1,
        // Drop-target hint — a tinted top border so the user can see
        // where their row will land.
        borderTop: dropTarget ? "2px solid var(--bsp-blue)" : undefined,
        background: dropTarget ? "rgba(27, 68, 156, 0.04)" : undefined,
      }}
      onDragOver={(e) => {
        if (dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver();
      }}
      onDragLeave={(e) => {
        // Only fire when leaving the row entirely — moving between
        // child cells fires dragleave too, which would clear the
        // highlight prematurely.
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        onDragLeave();
      }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
    >
      <td
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", `stage:${def.stageKey}`);
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        title="Drag to reorder"
        style={{
          cursor: busy ? "wait" : "grab",
          textAlign: "center",
          color: "var(--bsp-fg-muted)",
          userSelect: "none",
        }}
      >
        ⋮⋮
      </td>
      <td>
        <input
          className="stage-inp"
          style={{ width: "100%", fontFamily: "var(--bsp-font-mono)", fontWeight: 700, textAlign: "center" }}
          value={short}
          maxLength={8}
          disabled={busy}
          onChange={(e) => setShort(e.target.value)}
          onBlur={() => { if (short !== def.short && short) onPatch({ short }); }}
        />
      </td>
      <td>
        <input
          className="stage-inp"
          style={{ width: "100%" }}
          value={name}
          maxLength={64}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { if (name !== def.name && name) onPatch({ name }); }}
        />
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="color"
            value={color}
            disabled={busy}
            onChange={(e) => setColor(e.target.value)}
            onBlur={() => { if (color !== def.color) onPatch({ color }); }}
            style={{ width: 28, height: 22, border: "1px solid var(--bsp-border)", borderRadius: 3, padding: 0, background: "transparent" }}
          />
          <span style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 10, color: "var(--bsp-fg-muted)" }}>
            {color}
          </span>
        </div>
      </td>
      <td style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={def.hasHours}
          disabled={busy}
          onChange={(e) => onPatch({ hasHours: e.target.checked })}
        />
      </td>
      <td>
        <CapacityInput
          initial={def.weeklyCapacityHours}
          disabled={busy || !def.hasHours}
          onCommit={(v) => onPatch({ weeklyCapacityHours: v })}
        />
      </td>
      <td style={{ fontSize: 10, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {def.isCustom ? "Custom" : def.weld ? `Weld bay ${def.bay ?? ""}` : "Canonical"}
      </td>
      <td style={{ textAlign: "right" }}>
        <button
          type="button"
          className="btn-sm"
          onClick={onArchive}
          disabled={busy}
          style={def.archived ? undefined : { color: "var(--bsp-red-fg)" }}
        >
          {def.archived ? "Restore" : "Archive"}
        </button>
      </td>
    </tr>
  );
}

function CapacityInput({
  initial,
  disabled,
  onCommit,
}: {
  initial: number;
  disabled: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(initial));
  useEffect(() => { setDraft(String(initial)); }, [initial]);
  return (
    <input
      type="number"
      min={0}
      step={1}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = Number(draft);
        if (Number.isFinite(n) && n >= 0 && n !== initial) onCommit(n);
      }}
      style={{
        width: "100%",
        padding: "4px 6px",
        textAlign: "right",
        fontFamily: "var(--bsp-font-mono)",
        fontSize: 12,
      }}
    />
  );
}

function AddStageForm({ onAdd }: { onAdd: (input: StageConfigInputT) => Promise<void> }) {
  const [name, setName] = useState("");
  const [short, setShort] = useState("");
  const [color, setColor] = useState("#1B449C");
  const [hasHours, setHasHours] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !short.trim()) return;
    setBusy(true);
    try {
      await onAdd({
        name: name.trim(),
        short: short.trim().toUpperCase(),
        color,
        hasHours,
        weld: false,
      });
      setName("");
      setShort("");
      setColor("#1B449C");
      setHasHours(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 14,
      padding: 12,
      border: "1px dashed var(--bsp-border-strong)",
      borderRadius: "var(--bsp-r-md)",
      background: "var(--bsp-surface-muted)",
    }}>
      <div style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--bsp-blue)",
        marginBottom: 8,
      }}>Add custom stage</div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 180px", fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Name
          <input
            className="stage-inp"
            placeholder="e.g. Sandblasting"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={64}
            disabled={busy}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 80, fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Short
          <input
            className="stage-inp"
            style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700, textAlign: "center" }}
            placeholder="SB"
            value={short}
            onChange={(e) => setShort(e.target.value.toUpperCase())}
            maxLength={8}
            disabled={busy}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Colour
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={busy}
            style={{ width: 40, height: 28, border: "1px solid var(--bsp-border)", borderRadius: 3, padding: 0, background: "transparent" }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
          <input
            type="checkbox"
            checked={hasHours}
            onChange={(e) => setHasHours(e.target.checked)}
            disabled={busy}
          />
          Tracks hours
        </label>
        <button
          type="button"
          className="btn-submit"
          onClick={() => void submit()}
          disabled={busy || !name.trim() || !short.trim()}
        >
          {busy ? "Adding…" : "Add stage"}
        </button>
      </div>
    </div>
  );
}
